import { SparklesIcon } from "lucide-react";
import { useEffect, useRef } from "react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@/components/ui/button";

import { ReviewPortalToast, ReviewPortalToastKind } from "@/types";

const TOAST_DURATION_MS = 5_000;

export const ReviewPortalToastStack = ({
  onDismiss,
  onUndo,
  toasts,
}: {
  onDismiss: (toastId: string) => void;
  onUndo: (toast: ReviewPortalToast) => void;
  toasts: ReviewPortalToast[];
}) => {
  if (toasts.length === 0) return null;

  const render = () => (
    <Column className="pointer-events-none absolute bottom-4 right-4 z-50 items-end gap-2">
      {toasts.map((t) => (
        <ReviewPortalToastItem
          key={t.id}
          onDismiss={onDismiss}
          onUndo={onUndo}
          toast={t}
        />
      ))}
    </Column>
  );

  return render();
};

const ReviewPortalToastItem = ({
  onDismiss,
  onUndo,
  toast,
}: {
  onDismiss: (toastId: string) => void;
  onUndo: (toast: ReviewPortalToast) => void;
  toast: ReviewPortalToast;
}) => {
  // Hold latest callbacks via refs so the dismiss timer doesn't reset every
  // time the parent re-renders with new function identities.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    const timer = setTimeout(() => {
      onDismissRef.current(toast.id);
    }, TOAST_DURATION_MS);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id]);

  const candidateName = toast.candidateName.trim() || "candidate";

  /* Render */
  const renderAgentHandling = () => (
    <Row className="pointer-events-auto items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">
      <SparklesIcon className="h-3.5 w-3.5 shrink-0 text-blue-400" />
      <Text className="text-sm text-white">
        Stevie&apos;s reaching out to{" "}
        <span className="font-semibold text-white">{candidateName}</span>
      </Text>
    </Row>
  );

  const renderReject = () => (
    <Row className="pointer-events-auto items-center gap-3 rounded-lg border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-white shadow-lg">
      <Text className="text-sm text-white">
        You rejected{" "}
        <span className="font-semibold text-white">{candidateName}</span>
      </Text>
      <Button
        className="h-7 px-2 text-xs font-semibold uppercase tracking-wide text-primary-300 hover:bg-slate-800 hover:text-primary-200"
        onClick={() => {
          onUndo(toast);
        }}
        size="sm"
        type="button"
        variant="ghost"
      >
        Undo
      </Button>
    </Row>
  );

  if (toast.kind === ReviewPortalToastKind.AGENT_HANDLING)
    return renderAgentHandling();
  return renderReject();
};
